"use client"
import { Navigation } from "@/components/navigation";
import { useRef, useEffect, useState } from 'react'

import { track } from '@vercel/analytics'


type Report = {hidden: boolean, url: string}
type Reports = Record<string, {label: string, hidden: boolean, months: Record<string, Report>}>

function parseHash(reports: Reports): { reportKey: string; monthKey: string } | null {
  if (typeof window === "undefined") return null
  const hash = window.location.hash.slice(1)
  const [reportKey, monthKey] = hash.split("/")
  if (reportKey && monthKey && reports[reportKey]?.months[monthKey]) {
    return { reportKey, monthKey }
  }
  return null
}

export default function ResearchPage() {
  const [reportKey, setReportKey] = useState<string>("")
  const [monthKey, setMonthKey] = useState<string>("")
  const [reports, setReports] = useState<Reports | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    fetch("https://cf-reports.catenary.digital/reports.json")
      .then(r => r.json())
      .then((r: Reports) => setReports(r))
  }, [])

  useEffect(() => {
    if (!reports) return;
    const parsed = parseHash(reports)
    if (parsed) {
      setReportKey(parsed.reportKey)
      setMonthKey(parsed.monthKey)
      track('Report opened', {justOpened: true, userInput: true, ...parsed})
    } else {
      // NOTE: as per rajiv's request this is the one we should show by default
      const firstReport = "sky_fundamentals"
      const lastMonth = Object.keys(reports[firstReport]?.months ?? {}).at(-1) ?? ""
      setReportKey(firstReport)
      setMonthKey(lastMonth)
      track('Report opened', {justOpened: true, userInput: false, reportKey: firstReport, monthKey: lastMonth})
    }
  }, [reports])

  const currentMonths = (reports && reportKey) ? (reports[reportKey]?.months ?? {}) : {}
  const report = currentMonths[monthKey] ?? Object.values(currentMonths)[0] ?? ""

  useEffect(() => {
    if (iframeRef.current && report) {
      iframeRef.current.src = report.url
    }
  }, [report])

  useEffect(() => {
    if (reportKey && monthKey) {
      window.history.replaceState(null, "", `#${reportKey}/${monthKey}`)
    }
  }, [reportKey, monthKey])

  function handleReportChange(newReport: string) {
    setReportKey(newReport)
    const months = reports?.[newReport]?.months ?? {}
    const lastMonth = Object.keys(months).at(-1) ?? ""
    setMonthKey(lastMonth)

    track('Report opened', {justOpened: false, userInput: true, reportKey: newReport, monthKey: lastMonth})
  }

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Floating report selector */}
      <div className="fixed bottom-2 xl:absolute xl:bottom-auto xl:top-4 left-1/2 -translate-x-1/2 z-50 flex gap-3 items-center bg-background/80 backdrop-blur border border-border rounded-xl px-4 py-2 shadow-lg">
        <select
          value={reportKey}
          onChange={(e) => handleReportChange(e.target.value)}
          className="bg-transparent text-sm font-medium focus:outline-none cursor-pointer"
        >
          {Object.entries(reports ?? {}).map(([key, { label, hidden }]) => !hidden && (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <div className="w-px h-4 bg-border" />

        <select
          value={monthKey}
          onChange={(e) => {
            setMonthKey(e.target.value)
            track('Report opened', {justOpened: false, userInput: true, reportKey, monthKey: e.target.value})
          }}
          className="bg-transparent text-sm focus:outline-none cursor-pointer"
        >
          {Object.keys(currentMonths).filter(k => !currentMonths[k].hidden).map((month) => (
            <option key={month} value={month}>{month}</option>
          ))}
        </select>
      </div>

      <section>
        <iframe ref={iframeRef} className="min-h-[100vh] w-full" />
      </section>
    </div>
  )
}
